import React, { useState, useEffect } from 'react';

import Details from './Details';
import Panel from '../../../helpers/panel/Panel';
import PostBody from '../../../api/internal/PostBody';
import Close from '../../../helpers/panel/Close';
import Get from '../../../api/internal/Get';

function Truck({ GetTruckBoard }) {
  const [createDATPostOptions, setCreateDATPostOptions] =
    useState<CreateDATTruckPostOptions>();
  const [datEquipmentTypeName, setDatEquipmentTypeName] = useState('Van');
  const [datTruckPostParam, setDATTruckPostParam] =
    useState<CreateDATTruckPostParam>({
      datEquipmentCode: null,
      length: null,
      weight: null,
      originCity: null,
      originState: null,
      destinationCity: null,
      destinationState: null,
      destinationLat: null,
      destinationLong: null,
      earliestAvailableDate: new Date().toISOString().split('T')[0],
      latestAvailableDate: new Date().toISOString().split('T')[0],
      comment: null,
    });

  useEffect(() => {
    Get(`/DatService/GetCreateDatTruckPost`).then(response => {
      if (response) {
        setCreateDATPostOptions(response.data);
      }
    });
  }, []);

  async function PostAsset() {
    const response = await PostBody('DatService/PostTruckToDat', datTruckPostParam);
    if (response) {
      await GetTruckBoard();
      Close();
    }
  }

  const requirements = [
    {
      label: 'Equipment Type',
      FunctionBool: () => datTruckPostParam.datEquipmentCode,
      assignedValue: () => datEquipmentTypeName,
      missingDesc: 'Truck has not been assigned.',
    },
    {
      label: 'Origin',
      FunctionBool: () =>
        datTruckPostParam.originCity && datTruckPostParam.originState,
      assignedValue: () =>
        `${datTruckPostParam.originCity}, ${datTruckPostParam.originState}`,
      missingDesc: 'Origin has not been assigned.',
    },
    {
      label: 'Destination',
      FunctionBool: () => true,
      assignedValue: () =>
        datTruckPostParam.destinationCity && datTruckPostParam.destinationState
          ? `${datTruckPostParam.destinationCity}, ${datTruckPostParam.destinationState}`
          : 'Open',
      missingDesc: 'Destination has not been properly assigned.',
    },
    {
      label: 'Length',
      FunctionBool: (): any => datTruckPostParam.length,
      assignedValue: () => `${datTruckPostParam.length}'`,
      missingDesc: 'Length has not been assigned.',
    },
    {
      label: 'Weight',
      FunctionBool: (): any => datTruckPostParam.weight,
      assignedValue: () => `${datTruckPostParam.weight} lbs`,
      missingDesc: 'Weight has not been assigned.',
    },
    {
      label: 'Earliest Availability',
      FunctionBool: () => datTruckPostParam.earliestAvailableDate.toString(),
      assignedValue: () => {
        const parts = datTruckPostParam.earliestAvailableDate
          .toString()
          .split('-');
        return `${parts[1]}/${parts[2]}/${parts[0]}`;
      },
      missingDesc:
        'Earliest Available Date and Time have not both been assigned.',
    },
    {
      label: 'Latest Availability',
      FunctionBool: () => {
        // Because FunctionBool is expecting a type of string or number this is
        // the best way I could find to add a "boolean" check for if the selected
        // Date/Time is in the past or not.
        let PastCheck: string | null =
          datTruckPostParam.latestAvailableDate >=
          datTruckPostParam.earliestAvailableDate
            ? 'valid'
            : null;
        return datTruckPostParam.latestAvailableDate && PastCheck;
      },
      assignedValue: () => {
        const parts = datTruckPostParam.latestAvailableDate
          .toString()
          .split('-');
        return `${parts[1]}/${parts[2]}/${parts[0]}`;
      },
      missingDesc: `Latest Availability cannot be earlier than Earliest Availability`,
    },
  ];

  const pages = [
    {
      title: 'Details',
      displayedIcon: '',
      content: (
        <Details
          draftDATPostParam={datTruckPostParam}
          createDATPostOptions={createDATPostOptions}
          UpdateDraftDATPost={UpdateDraftDATPost}
          setDatEquipmentTypeName={setDatEquipmentTypeName}
        />
      ),
      require: requirements,
    },
  ];

  function OnSubmit() {
    let fulfillsFormRequirements = true;
    pages.forEach((_, index) => {
      if (!FulFillsPageRequirements(index)) {
        fulfillsFormRequirements = false;
      }
    });
    if (fulfillsFormRequirements) {
      PostAsset();
    } else alert('Not all info included');
  }

  function UpdateDraftDATPost(newValue: string, attributeName: string) {
    setDATTruckPostParam(prev => ({
      ...prev,
      [attributeName]:
        newValue?.toString().trim().length !== 0 ? newValue : null,
    }));
  }

  function FulFillsPageRequirements(pageIndex) {
    let fulfillsRequirements = true;
    pages[pageIndex].require.forEach(requirement => {
      if (!requirement.FunctionBool()) {
        fulfillsRequirements = false;
      }
    });
    return fulfillsRequirements;
  }

  return <Panel OnSubmit={OnSubmit} title="Post a Truck" pages={pages} />;
}

export default Truck;
